import type { ActiveTimer, OpenWorkRecordInfo, RecordSegments, StartableTask, WorkLog } from '../../types';
import type { DailyStatusTicket } from '../../types/workspace';

/**
 * Pure helpers for starting a timer and picking "my recent tasks"
 * (owner 2026-10-11). Unit-tested in `utils/__tests__/timerModel.test.ts`.
 */

/**
 * D-270: where a timer's live clock counts from. A task continued today (after
 * a break, or ⏸ then ▶) counts on from the time it already had today (the
 * server's `accumulated_today_seconds`); any other timer from its own start.
 */
export function timerClockStart(tm: Pick<ActiveTimer, 'started_at' | 'accumulated_today_seconds'>): string {
  const prior = Number(tm.accumulated_today_seconds ?? 0);
  if (!(prior > 0)) return tm.started_at;
  return new Date(new Date(tm.started_at).getTime() - prior * 1000).toISOString();
}

/**
 * The "התחלה" button of a task page: the same rule as the "התחל עבודה" window
 * (GET /timer/tasks, `startableTasksWhere`) — a task the user is on, as the
 * accountable owner (`assigned_agent_id`) or an assigned worker (`agents`),
 * neither completed nor cancelled. The server refuses any other start.
 */
export function canStartTimerOn(
  task: { task_type?: string | null; status?: string | null; cancelled_at?: string | null; assigned_agent_id?: string | null; agents?: ReadonlyArray<{ id: string }> | null },
  userId: string | null | undefined,
): boolean {
  if (!userId) return false;
  if (task.task_type && task.task_type !== 'task') return false;
  if (task.status === 'completed' || task.status === 'closed' || task.cancelled_at) return false;
  return task.assigned_agent_id === userId || (task.agents ?? []).some((a) => a.id === userId);
}

/**
 * FU-28 — may a task-table row show ▶? A TASK: the canStartTimerOn rule (the
 * user is on it). A service ticket in a mixed list: open is enough (D-47).
 */
export function canRowStartTimer(
  row: { task_type?: string | null; status?: string | null; cancelled_at?: string | null; assigned_agent_id?: string | null; agents?: ReadonlyArray<{ id: string }> | null },
  userId: string | null | undefined,
): boolean {
  if (row.task_type && row.task_type !== 'task') return !!userId && row.status !== 'completed' && row.status !== 'closed' && !row.cancelled_at;
  return canStartTimerOn(row, userId);
}

/** Running timers on OTHER tasks: when any exist, starting asks "stop and switch" or "run both". */
export function otherRunningTimers(timers: readonly ActiveTimer[], taskId: string, runAlongside: readonly string[] = []): ActiveTimer[] {
  // `runAlongside`: tasks the user already chose to run together (the break
  // resume starts several at once) — they never trigger the question.
  return timers.filter((tm) => tm.status === 'running' && tm.ticket_id !== taskId && !runAlongside.includes(tm.ticket_id));
}

/**
 * FIX-P37 (T-69) — the running timers that trigger "עצירה ומעבר / להריץ את שניהם".
 * "אני על זה" pauses the running TASK timers by itself (Q-182), so it asks only
 * when ANOTHER "אני על זה" runs; every other start asks about every other timer.
 */
export function switchCandidates(
  timers: readonly ActiveTimer[],
  taskId: string,
  opts: { reply?: boolean; runAlongside?: readonly string[] } = {},
): ActiveTimer[] {
  const others = otherRunningTimers(timers, taskId, opts.runAlongside);
  return opts.reply ? others.filter((tm) => tm.kind === 'reply') : others;
}

/**
 * FIX-P37 — stopping "אני על זה" to switch to ANOTHER task: the task it paused
 * does not start running by itself. Switching to another "אני על זה" keeps the
 * normal stop (the new reply pauses what runs, as the first one did).
 */
export function resumePausedOnSwitch(stopped: Pick<ActiveTimer, 'kind'>, startingReply: boolean): boolean {
  return !(stopped.kind === 'reply' && !startingReply);
}

/** A task the user worked on lately (or owns now), for the top of a task picker. */
export interface RecentTask {
  id: string;
  title: string;
  /** 'task' | 'ticket' when known (timers and open records do not say). */
  task_type?: string | null;
  /** Where it lives: customer · project (or, with no project, its engagement), when known. */
  meta?: string;
  /** T-50 (a) — the task's status when known, so a picker can refuse a completed task in an approved month up front. */
  status?: string | null;
}

export interface RecentTasksInput {
  timers?: readonly ActiveTimer[];
  openRecords?: readonly OpenWorkRecordInfo[];
  /** Work logs already in the client cache (any user; filtered by `userId`). */
  workLogs?: readonly WorkLog[];
  /** "המיקוד שלי": the user's open tasks, already in priority order. */
  focus?: readonly DailyStatusTicket[];
  userId?: string | null;
  limit?: number;
}

const joinMeta = (...parts: Array<string | null | undefined>) => parts.filter(Boolean).join(' · ') || undefined;

/**
 * Most recent first, no duplicates: running timers, then suspended timers and
 * open work records, then the user's own latest work logs, then the focus list.
 * A completed or cancelled task is never offered (wherever its status is known).
 */
export function buildRecentTasks({ timers = [], openRecords = [], workLogs = [], focus = [], userId, limit = 6 }: RecentTasksInput): RecentTask[] {
  const out: RecentTask[] = [];
  const seen = new Set<string>();
  const closed = new Set<string>();
  const isDone = (status: string | null | undefined) => status === 'completed' || status === 'closed';
  const statusById = new Map<string, string>();
  const noteStatus = (id: string | null | undefined, status: string | null | undefined) => { if (id && status) statusById.set(id, status); };
  for (const tm of timers) noteStatus(tm.ticket_id, tm.ticket_status);
  for (const rec of openRecords) noteStatus(rec.ticket_id, rec.ticket_status);
  for (const wl of workLogs) noteStatus(wl.ticket_id, wl.ticket_status);
  for (const f of focus) noteStatus(f.id, f.status);
  for (const tm of timers) if (isDone(tm.ticket_status)) closed.add(tm.ticket_id);
  // QA-3 #8 — a cancelled task's record stays open (SEC-1), but it is not offered.
  for (const rec of openRecords) if (isDone(rec.ticket_status) || rec.ticket_cancelled === true) closed.add(rec.ticket_id);
  for (const wl of workLogs) if (wl.ticket_id && (isDone(wl.ticket_status) || wl.ticket_cancelled === true)) closed.add(wl.ticket_id);
  for (const f of focus) if (isDone(f.status)) closed.add(f.id);
  const add = (item: RecentTask) => {
    if (!item.id || !item.title || seen.has(item.id) || closed.has(item.id)) return;
    seen.add(item.id);
    out.push(item);
  };

  const focusById = new Map(focus.map((f) => [f.id, f]));
  const enrich = (id: string, title: string | null | undefined): RecentTask => {
    const f = focusById.get(id);
    return {
      id,
      title: title || f?.title || '',
      task_type: f?.task_type ?? null,
      status: statusById.get(id) ?? null,
      // P-02b (§6.6): a task with no project shows its engagement.
      meta: f ? joinMeta(f.organization_name, f.project_name || f.engagement_name) : undefined,
    };
  };

  for (const tm of timers) if (tm.status === 'running') add(enrich(tm.ticket_id, tm.ticket_title));
  for (const tm of timers) if (tm.status !== 'running') add(enrich(tm.ticket_id, tm.ticket_title));
  for (const rec of openRecords) add(enrich(rec.ticket_id, rec.ticket_title));

  const mine = workLogs
    .filter((wl) => wl.ticket_id && (!userId || wl.agent_id === userId))
    .slice()
    .sort((a, b) => (b.start_time ?? '').localeCompare(a.start_time ?? ''));
  for (const wl of mine) {
    const base = enrich(wl.ticket_id, wl.ticket_title);
    add({
      ...base,
      task_type: base.task_type ?? wl.task_type ?? null,
      meta: base.meta ?? joinMeta(wl.organization_name ?? wl.customer_name, undefined),
    });
  }

  for (const f of focus) add({ id: f.id, title: f.title, task_type: f.task_type, status: f.status ?? null, meta: joinMeta(f.organization_name, f.project_name || f.engagement_name) });

  return out.slice(0, limit);
}

/**
 * Work logs out of react-query cache entries of any shape the app uses
 * (`ApiResponse<WorkLog[]>`, a bare array, or infinite pages of either).
 */
export function workLogsFromCache(entries: readonly unknown[]): WorkLog[] {
  const out: WorkLog[] = [];
  const take = (v: unknown) => {
    if (Array.isArray(v)) {
      for (const x of v) {
        const wl = x as Partial<WorkLog> | null;
        if (wl && typeof wl === 'object' && typeof wl.ticket_id === 'string' && typeof wl.start_time === 'string') out.push(wl as WorkLog);
      }
      return;
    }
    if (v && typeof v === 'object') {
      const o = v as { data?: unknown; pages?: unknown[] };
      if (Array.isArray(o.pages)) o.pages.forEach(take);
      else if ('data' in o) take(o.data);
    }
  };
  entries.forEach(take);
  return out;
}

// ---------------------------------------------------------------------------
// P-10a — the one timer flow (D-63 … D-67)
// ---------------------------------------------------------------------------

/** The server refused a start for want of a clock-in today (D-66, 409). */
export function isClockInRequired(err: unknown): boolean {
  const e = err as { error?: { error_code?: string } } | null;
  return e?.error?.error_code === 'CLOCK_IN_REQUIRED';
}

/** QX-1283 — the start was refused because the 00:00 automatic clock-out closed yesterday's shift. */
export function isAutoClockoutRefusal(err: unknown): boolean {
  const e = err as { error?: { error_code?: string; auto_clockout?: boolean } } | null;
  return e?.error?.error_code === 'CLOCK_IN_REQUIRED' && e.error.auto_clockout === true;
}

/** D-146 (Q-98) — one engagement the hours of a ticket may go to.
 *  D-300: `warranty` = a project / development in its warranty period (no billing); `warranty_until` = the
 *  last day of the warranty of a completed one (null while the project is open). */
export interface WorkEngagementOption { id: string; type: string; name: string | null; warranty?: boolean; warranty_until?: string | null; warranty_ended_from?: string }

/**
 * D-302 — the question of the engagement dialog, by the options the server sent:
 *  - QX-1307 (the request's project ended its warranty period, `warranty_ended_from`): "the warranty of <project>
 *    ended - where to log from now?";
 *  - QX-1310 (a project in its warranty period is offered): "is the request about the project <name> (warranty, not
 *    billed) or the hour bank?" - the D-302 wording;
 *  - otherwise the question as before.
 * In the two D-302 cases nothing is chosen in advance (a quick click must not log on the billed engagement).
 */
export function engagementAsk(options: readonly WorkEngagementOption[]): {
  key: 'timer.engagement.bodyEnded' | 'timer.engagement.bodyWarranty' | null;
  project: string;
  service: 'hour_bank' | 'service_contract' | 'service_without_contract' | 'other';
  preselect: boolean;
} {
  const ended = options.find((o) => typeof o.warranty_ended_from === 'string');
  const warranty = options.filter((o) => o.warranty === true);
  const services = options.filter((o) => o.warranty !== true && ['hour_bank', 'service_contract', 'service_without_contract'].includes(o.type));
  const types = Array.from(new Set(services.map((o) => o.type)));
  const service = types.length === 1 ? (types[0] as 'hour_bank' | 'service_contract' | 'service_without_contract') : 'other';
  if (ended) return { key: 'timer.engagement.bodyEnded', project: ended.warranty_ended_from || '', service, preselect: false };
  if (warranty.length > 0) {
    return { key: 'timer.engagement.bodyWarranty', project: warranty.map((o) => o.name || '').filter(Boolean).join(' / '), service, preselect: false };
  }
  return { key: null, project: '', service, preselect: true };
}

/** D-300 — the tag next to a warranty option: null for any other option. */
export function warrantyTag(o: Pick<WorkEngagementOption, 'warranty' | 'warranty_until'>): { key: 'timer.engagement.warranty' | 'timer.engagement.warrantyUntil'; until: string | null } | null {
  if (o.warranty !== true) return null;
  return o.warranty_until ? { key: 'timer.engagement.warrantyUntil', until: o.warranty_until } : { key: 'timer.engagement.warranty', until: null };
}

/**
 * D-146 (Q-98) — the server refused a start: the ticket has no engagement and
 * the customer has no single active service engagement. Returns the options
 * to ask from (possibly empty), or null for any other error.
 */
export function engagementRequiredOptions(err: unknown): WorkEngagementOption[] | null {
  const e = err as { error?: { error_code?: string; details?: { engagements?: WorkEngagementOption[] } } } | null;
  if (e?.error?.error_code !== 'ENGAGEMENT_REQUIRED') return null;
  return Array.isArray(e.error.details?.engagements) ? e.error.details!.engagements! : [];
}

/** D-98 — the server refused a start because the employee is on a break (409). */
export function isOnBreakError(err: unknown): boolean {
  const e = err as { error?: { error_code?: string } } | null;
  return e?.error?.error_code === 'ON_BREAK';
}

/**
 * D-66 / D-98 — what to ask before a start, from the attendance status:
 * not clocked in → the clock-in question; on a break → "לסיים את ההפסקה?".
 */
export function attendanceQuestion(status: string | null | undefined): 'clock_in' | 'end_break' | null {
  if (status === 'clocked_out') return 'clock_in';
  if (status === 'on_break') return 'end_break';
  return null;
}

/**
 * D-64 — after ⏸ the task's status circles show for a moment (tasks only:
 * service tickets stay as they are, D-47). Null = nothing to show.
 */
export function statusAfterStop(timer: Pick<ActiveTimer, 'ticket_id' | 'ticket_title' | 'ticket_status' | 'task_type' | 'ticket_progress'>): { ticketId: string; title: string; status: string; progress: number | null } | null {
  if (timer.task_type !== 'task' || !timer.ticket_status) return null;
  return { ticketId: timer.ticket_id, title: timer.ticket_title ?? '', status: timer.ticket_status, progress: timer.ticket_progress ?? null };
}

/**
 * D-64 — the stops of one log as "09:00–09:42", in order; a part still
 * running ends with "…". `clock` formats an ISO instant as HH:MM.
 */
export function formatStops(rec: Pick<RecordSegments, 'segments' | 'running'> | null | undefined, clock: (iso: string) => string): string[] {
  if (!rec) return [];
  const out = rec.segments.map((s) => `${clock(s.start_time)}–${clock(s.end_time)}`);
  if (rec.running) {
    const end = rec.running.status === 'suspended' && rec.running.suspended_at ? clock(rec.running.suspended_at) : '…';
    out.push(`${clock(rec.running.started_at)}–${end}`);
  }
  return out;
}

export interface StartableView {
  /** "אחרונות": worked on lately, newest first. */
  recent: StartableTask[];
  /** "כל המשימות שלי (פתוחות)": the rest. */
  rest: StartableTask[];
  /** The customers of my open tasks, for the "לקוח" filter (by name). */
  customers: Array<{ id: string; name: string }>;
}

/**
 * D-67 — split my open tasks for the "התחל עבודה" window. The list comes from
 * the server already limited to MY open tasks; here the customer filter and the
 * search narrow it, and the latest worked ones go on top.
 */
export function splitStartableTasks(
  tasks: readonly StartableTask[],
  f: { organizationId?: string | null; search?: string | null } = {},
  recentLimit = 5
): StartableView {
  const byOrg = new Map<string, string>();
  for (const t of tasks) if (t.organization_id) byOrg.set(t.organization_id, t.organization_name ?? '');
  const customers = Array.from(byOrg, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'he'));

  const q = (f.search ?? '').trim().toLowerCase();
  const shown = tasks.filter((t) => {
    if (f.organizationId && t.organization_id !== f.organizationId) return false;
    if (!q) return true;
    return [t.title, t.organization_name, t.container_name, t.milestone_name, t.ticket_number != null ? String(t.ticket_number) : '']
      .some((v) => (v ?? '').toLowerCase().includes(q));
  });
  const recent = shown
    .filter((t) => !!t.last_worked_at)
    .sort((a, b) => (b.last_worked_at ?? '').localeCompare(a.last_worked_at ?? ''))
    .slice(0, recentLimit);
  const recentIds = new Set(recent.map((t) => t.id));
  return { recent, rest: shown.filter((t) => !recentIds.has(t.id)), customers };
}

/** "אקמה › אתר חדש › פיתוח": customer › project (or engagement) › milestone. */
export function startableTaskPath(t: Pick<StartableTask, 'organization_name' | 'container_name' | 'milestone_name'>): string {
  return [t.organization_name, t.container_name, t.milestone_name].filter(Boolean).join(' › ');
}

/**
 * HN-3: which button a timer row in the timers panel shows. A running timer offers pause (⏸);
 * one that is not running (suspended by a break or by "עצירה ומעבר") offers resume (▶) -
 * the row used to keep a disabled ⏸, which looked stopped all the time.
 */
export function timerRowAction(status: string | null | undefined): 'pause' | 'resume' {
  return status === 'running' ? 'pause' : 'resume';
}

/**
 * FIX-OPENREC — who sees the timer (the floating capsule, the timers panel, "רשומות פתוחות").
 * The server lets any OTE employee start / stop (`requireOteStaff`), whatever the login role,
 * so the UI follows the same identity: an employee by `isOteEmployee` (GET /permissions/me) sees it
 * whatever the role; admin / agent keep seeing it as before (additive, D-253). A portal customer
 * (customer role, not an employee) never does.
 */
export function canUseTimer(role: string | null | undefined, isOteEmployee: boolean | undefined): boolean {
  return role === 'admin' || role === 'agent' || isOteEmployee === true;
}

/**
 * FIX-OPENREC — the timer status failed to load (error / timeout) and nothing is cached:
 * the capsule stays, with "לא ניתן לטעון טיימרים, לנסות שוב", instead of vanishing silently.
 */
export function timerLoadView(input: { isLoading: boolean; isError: boolean; hasData: boolean }): 'loading' | 'error' | 'ready' {
  if (input.hasData) return 'ready';
  if (input.isError) return 'error';
  return input.isLoading ? 'loading' : 'ready';
}

/**
 * FIX-OPENREC — Home hides the floating capsule, so with no running timer the open work
 * records ("חידוש" / "סיום ותיעוד") show in the header: one record as its row, several as
 * "N רשומות פתוחות" that opens the timers panel. A running timer keeps the header (it wins).
 */
export function homeOpenRecordsView(input: { isStaff: boolean; loading: boolean; timerRunning: boolean; openCount: number }): 'none' | 'single' | 'many' {
  if (!input.isStaff || input.loading || input.timerRunning || input.openCount <= 0) return 'none';
  return input.openCount === 1 ? 'single' : 'many';
}

/**
 * FIX-TIMER-START-EDIT: the new start of a timer from an "HH:mm" the user typed,
 * on the SAME local day as its current start (only the time is editable).
 * null for a malformed value. The server validates the rest (future, overlap, locked month).
 */
export function timerStartFromTime(currentStartIso: string, hhmm: string): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  const d = new Date(currentStartIso);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(h, min, 0, 0);
  return d.toISOString();
}
